import { useCallback, useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, StatusBadge, InfoRow, NativeSelect } from "@/components/common";
import { TotpSetup, EmailEnroll, RecoveryCodesDialog, FactorDialog, CodeInput } from "@/components/security";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";

function EmailVerifyRow({ sec, reload }) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  if (sec.email_verified) return <StatusBadge status="verified" testId="email-verified-badge">Verified</StatusBadge>;
  return (
    <div className="flex flex-col items-end gap-2">
      <StatusBadge status="pending" testId="email-verified-badge">Not verified</StatusBadge>
      {!sent ? (
        <Button size="sm" variant="outline" data-testid="verify-email-button" onClick={async () => {
          try { await api.post("/me/email-verification/send"); setSent(true); toast.success("Verification code sent"); } catch (e) { toast.error(errMsg(e)); }
        }}>Verify email</Button>
      ) : (
        <div className="flex gap-2 w-64">
          <CodeInput value={code} onChange={setCode} testId="verify-email-code-input" />
          <Button size="sm" data-testid="verify-email-confirm-button" onClick={async () => {
            try { await api.post("/me/email-verification/confirm", { code }); toast.success("Email verified"); reload(); } catch (e) { toast.error(errMsg(e)); }
          }}>OK</Button>
        </div>
      )}
    </div>
  );
}

function Sessions() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/auth/sessions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  return (
    <Panel title="Active sessions" description="Web and mobile sessions using the shared authentication service." testId="sessions-panel">
      <div className="divide-y divide-slate-100">
        {items.map((s) => (
          <div key={s.id} className="flex items-center justify-between py-3 text-sm">
            <div><p className="font-medium capitalize">{s.client} {s.current && <span className="text-amber-700">(this session)</span>}</p>
              <p className="text-xs text-slate-500">{fmtDate(s.created_at)} · {s.user_agent?.slice(0, 60)}</p></div>
            {!s.current && <Button size="sm" variant="outline" onClick={async () => { await api.delete(`/auth/sessions/${s.id}`); load(); }} data-testid={`revoke-session-${s.id}`}>Revoke</Button>}
          </div>
        ))}
      </div>
    </Panel>
  );
}

export default function Profile() {
  const { user, reloadUser, requestReauth } = useAuth();
  const [sec, setSec] = useState(null);
  const [mode, setMode] = useState(null); // 'totp' | 'email'
  const [codes, setCodes] = useState(null);
  const [factor, setFactor] = useState(null); // {action}
  const load = useCallback(async () => { const { data } = await api.get("/me/security"); setSec(data); reloadUser(); }, [reloadUser]);
  useEffect(() => { api.get("/me/security").then((r) => setSec(r.data)); }, []);
  if (!sec) return null;
  const enrolled = [sec.email_enrolled && "email", sec.totp_enrolled && "totp"].filter(Boolean);

  const ensureReauth = async () => {
    if (sec.recent_auth) return true;
    const ok = await requestReauth();
    if (ok) setSec({ ...sec, recent_auth: true });
    return ok;
  };
  const openSetup = async (m) => {
    if (m === "totp" && sec.totp_enrolled) { if (await ensureReauth()) setFactor({ action: "replace_totp" }); return; }
    if (await ensureReauth()) setMode(m);
  };
  const done = async (result) => {
    setMode(null);
    toast.success("Two-Factor Authentication updated");
    await load();
    // let the setup dialog finish closing before the recovery codes dialog opens
    if (result?.recovery_codes?.length) setTimeout(() => setCodes(result.recovery_codes), 250);
  };

  const runFactor = async (method, code) => {
    const a = factor.action;
    if (a === "disable") { await api.post("/me/2fa/disable", { method, code }); toast.success("Two-Factor Authentication disabled"); }
    if (a === "regen") { const { data } = await api.post("/me/2fa/recovery-codes/regenerate", { method, code }); setCodes(data.recovery_codes); }
    if (a === "preferred") { await api.put("/me/2fa/preferred", { method, code, preferred_method: factor.value }); toast.success("Preferred method updated"); }
    if (a === "replace_totp") {
      setFactor(null);
      setMode({ replace: { current_method: method, current_code: code } });
      return;
    }
    setFactor(null);
    await load();
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Account" title="Profile & Security" description="Manage your sign-in protection. Settings shown depend on your role and CASURECO II security policy." />
      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="Profile" className="lg:col-span-1" testId="profile-panel">
          <InfoRow label="Name">{user.name}</InfoRow>
          <InfoRow label="Role" testId="profile-role">{user.role_label}</InfoRow>
          <InfoRow label="Email">{user.email}</InfoRow>
          <InfoRow label="Member since">{fmtDate(user.created_at)}</InfoRow>
        </Panel>
        <Panel title="Security · Two-Factor Authentication (2FA)" className="lg:col-span-2" testId="security-section"
          description="One-time passwords (OTP) are used as the second verification step after your password.">
          {sec.required_by_policy && (
            <div className="flex gap-2 items-start text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 mb-4" data-testid="2fa-required-notice">
              <ShieldAlert className="h-4 w-4 mt-0.5" /> 2FA is mandatory for your role. It cannot be disabled from your profile.
            </div>
          )}
          <InfoRow label="Two-Factor Authentication" testId="2fa-status">
            <StatusBadge status={sec.enabled ? "enabled" : "disabled"}>{sec.enabled ? "Enabled" : "Disabled"}</StatusBadge>
          </InfoRow>
          <InfoRow label="Preferred verification method">
            {enrolled.length > 1 ? (
              <NativeSelect testId="preferred-method-select" className="w-48" value={sec.preferred_method}
                onChange={(v) => v !== sec.preferred_method && setFactor({ action: "preferred", value: v })}
                options={enrolled.map((m) => ({ value: m, label: m === "email" ? "Email OTP" : "Authenticator App" }))} />
            ) : <span data-testid="preferred-method">{sec.preferred_method === "totp" ? "Authenticator App" : sec.preferred_method === "email" ? "Email OTP" : "—"}</span>}
          </InfoRow>
          <InfoRow label="Registered email">{sec.email}</InfoRow>
          <InfoRow label="Email verification status"><EmailVerifyRow sec={sec} reload={load} /></InfoRow>
          {sec.available_methods.includes("email") && (
            <InfoRow label="Email OTP">
              <div className="flex items-center gap-2">
                <StatusBadge status={sec.email_enrolled ? "enabled" : "disabled"} testId="email-otp-status">{sec.email_enrolled ? "Enrolled" : "Not enrolled"}</StatusBadge>
                {!sec.email_enrolled && <Button size="sm" variant="outline" disabled={!sec.email_verified} onClick={() => openSetup("email")} data-testid="configure-email-otp-button">Configure Email OTP</Button>}
              </div>
            </InfoRow>
          )}
          {sec.available_methods.includes("totp") && (
            <InfoRow label="Authenticator app">
              <div className="flex items-center gap-2">
                <StatusBadge status={sec.totp_enrolled ? "enabled" : "disabled"} testId="totp-status">{sec.totp_enrolled ? "Enrolled" : "Not enrolled"}</StatusBadge>
                <Button size="sm" variant="outline" onClick={() => openSetup("totp")} data-testid="configure-totp-button">{sec.totp_enrolled ? "Replace authenticator" : "Configure Authenticator App"}</Button>
              </div>
            </InfoRow>
          )}
          <InfoRow label="Last successful 2FA" testId="last-2fa">{fmtDate(sec.last_verified_at)}</InfoRow>
          {sec.recovery_codes_enabled && (
            <InfoRow label="Recovery codes">
              <div className="flex items-center gap-2">
                <span data-testid="recovery-codes-remaining">{sec.enabled ? `${sec.recovery_codes_remaining} unused` : "—"}</span>
                {sec.enabled && <Button size="sm" variant="outline" onClick={async () => (await ensureReauth()) && setFactor({ action: "regen" })} data-testid="regenerate-codes-button">Regenerate</Button>}
              </div>
            </InfoRow>
          )}
          {sec.available_methods.length === 0 && !sec.enabled && <p className="text-sm text-slate-500 mt-3">2FA enrollment is currently not offered by system policy.</p>}
          <div className="flex gap-2 mt-5">
            {!sec.enabled && sec.available_methods.length > 0 && (
              <Button onClick={() => openSetup(sec.available_methods.includes("totp") ? "totp" : "email")} data-testid="enable-2fa-button">Enable 2FA</Button>
            )}
            {sec.can_disable && (
              <Button variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={async () => (await ensureReauth()) && setFactor({ action: "disable" })} data-testid="disable-2fa-button">Disable 2FA</Button>
            )}
          </div>
        </Panel>
      </div>
      <Sessions />

      <Dialog open={!!mode} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent className="max-w-xl" data-testid="setup-dialog">
          <DialogHeader><DialogTitle>{mode === "email" ? "Configure Email OTP" : "Configure Authenticator App"}</DialogTitle>
            <DialogDescription>2FA is turned on only after you enter a valid code.</DialogDescription></DialogHeader>
          {mode && mode !== "email" && (
            <TotpSetup onCancel={() => setMode(null)}
              start={async () => (await api.post("/me/2fa/totp/start", (typeof mode === "object" && mode.replace) || {})).data}
              confirm={async (code) => done((await api.post("/me/2fa/totp/confirm", { code })).data)} />
          )}
          {mode === "email" && (
            <EmailEnroll email={sec.email} onCancel={() => setMode(null)}
              start={async () => (await api.post("/me/2fa/email/start")).data}
              confirm={async (step, code) => { const { data } = await api.post("/me/2fa/email/confirm", { code }); await done(data); return data; }} />
          )}
        </DialogContent>
      </Dialog>
      <FactorDialog open={!!factor} methods={enrolled} onClose={() => setFactor(null)} onSubmit={runFactor}
        title={{ disable: "Disable Two-Factor Authentication", regen: "Regenerate recovery codes", preferred: "Change preferred method", replace_totp: "Replace authenticator" }[factor?.action]}
        description="Enter a current one-time code or a recovery code to confirm this change." />
      <RecoveryCodesDialog codes={codes} onClose={() => setCodes(null)} />
    </div>
  );
}
